import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker } from './shared';

/**
 * ICON
 *
 * Icons come from lucide-react. This recipe only controls size and colour —
 * which glyph to use stays a per-call-site decision.
 *
 * Sizes are their own token scale (--icon-size-*) rather than reusing the text
 * scale, because an icon usually needs to be a little larger than the text it
 * sits beside to look the same weight.
 */
export const iconRecipe = cva([uiMarker, 'shrink-0'], {
  variants: {
    size: {
      xs: 'size-icon-xs',
      sm: 'size-icon-sm',
      md: 'size-icon-md',
      lg: 'size-icon-lg',
      xl: 'size-icon-xl',
    },
    tone: {
      /** Take the colour of the surrounding text. The usual choice. */
      inherit: '',
      default: 'text-foreground',
      muted: 'text-muted-foreground',
      primary: 'text-primary',
      success: 'text-success',
      warning: 'text-warning',
      info: 'text-info',
      destructive: 'text-destructive',
    },
    spin: {
      true: 'animate-spin',
      false: '',
    },
    /** Filled with its own colour — a starred favourite, a solid heart. */
    filled: {
      true: 'fill-current',
      false: '',
    },
  },
  defaultVariants: {
    size: 'sm',
    tone: 'inherit',
    spin: false,
    filled: false,
  },
});

export type IconRecipeProps = VariantProps<typeof iconRecipe>;

/**
 * The tinted tile an icon sits inside — empty states, feature rows,
 * integration cards, notification lists.
 *
 * Two things here are shaped by what the product actually does. The size is a
 * number on the 4px grid rather than a t-shirt name, matching the gap scale:
 * the app uses 16, 20, 28, 32, 36, 40, 48 and 56px tiles, and no three-value
 * scale was ever going to cover that.
 *
 * And `tone` sets the *background only*. It used to set the icon colour too,
 * which sounds tidier until you look at the call sites: an integration card
 * draws an info-blue tile with a success-green tick to mean "connected". The
 * tile and the glyph carry different information, and a component that welds
 * them together cannot say that.
 */
const TILE_SIZES = {
  4: 'size-4',
  5: 'size-5',
  6: 'size-6',
  7: 'size-7',
  8: 'size-8',
  9: 'size-9',
  10: 'size-10',
  12: 'size-12',
  14: 'size-14',
  16: 'size-16',
} as const;

export type IconTileSize = keyof typeof TILE_SIZES;

/** An icon that fills roughly half its tile reads right at every size. */
export const ICON_FOR_TILE = {
  4: 'xs',
  5: 'xs',
  6: 'sm',
  7: 'sm',
  8: 'sm',
  9: 'md',
  10: 'md',
  12: 'lg',
  14: 'lg',
  16: 'xl',
} as const;

export const iconTileRecipe = cva(
  [uiMarker, 'inline-flex shrink-0 items-center justify-center'],
  {
    variants: {
      size: TILE_SIZES,
      shape: {
        square: 'rounded-lg',
        circle: 'rounded-full',
      },
      tone: {
        /** No background — the tile is doing sizing and centring only. */
        none: '',
        neutral: 'bg-muted',
        primary: 'bg-primary/10',
        success: 'bg-success-subtle',
        warning: 'bg-warning-subtle',
        info: 'bg-info-subtle',
        destructive: 'bg-destructive-subtle',
        /**
         * Filled primary with the glyph in white: the product's own mark in
         * a header, the check on a chosen card, a feature that is switched
         * on. The glyph inherits the colour, so `iconTone` is not needed.
         */
        active: 'bg-primary text-primary-foreground',
      },
    },
    defaultVariants: { size: 10, shape: 'square', tone: 'neutral' },
  }
);

export type IconTileRecipeProps = VariantProps<typeof iconTileRecipe>;
